import type {
  ApiError,
  HealthResponse,
  ParsedPlayerInfo,
  PlayerInfoResponse,
  ReportResponse,
  TalentBuild,
  WclFight,
  WclPlayer,
} from '@shared/types';
import { NAXX_ENCOUNTER_IDS } from '@shared/types';
import { parseCombatantInfo } from '@shared/parsing';
import {
  renderAuras,
  renderGear,
  renderStats,
  renderTalents,
  renderAssumedPicker,
  loadAssumedState,
  activeAssumedSpellIds,
  activeAssumedEffects,
  type AssumedState,
} from './render';
import { renderAbilityBreakdown } from './analysisRender';
import { renderRagePanel } from './rageRender';
import { renderCooldownPanel } from './cooldownRender';
import { renderDebuffPanel } from './debuffRender';
import { renderRotationPanel } from './rotationRender';
import { renderDiagnosis, fillRankings, type RankingsFillData } from './diagnosisRender';
import type { DiagnosisReport } from '@shared/diagnosis';
import type { AnalysisResponse } from '@shared/analysis';
import { extractGearStats, type GearStatBonuses } from '@shared/charSheet';
import { lookupEnchant } from '@shared/enchants';
import { raidBuffIcon } from '@shared/raidBuffs';
import { lookup } from './wowhead';
import { detectLang, getLang, setLang, t, tBossName, type Lang } from '@shared/i18n';

// ---- DOM helpers ----
function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

// ---- Two-column layout (compare two warriors side-by-side) ----
//
// All per-warrior DOM elements are wrapped in <div id="col-a"> with IDs
// suffixed `-a`. At startup we clone col-a into col-b, retaining its
// structure but rewriting all `-a` IDs to `-b`. The user opts into the
// comparison view by picking a second warrior in #warrior-select-b.
type Slot = 'a' | 'b';
const SLOTS: readonly Slot[] = ['a', 'b'];

function slotEl<T extends HTMLElement>(slot: Slot, base: string): T {
  return el<T>(`${base}-${slot}`);
}

function cloneColumnB(): void {
  const colA = document.getElementById('col-a');
  if (!colA) throw new Error('col-a missing');
  if (document.getElementById('col-b')) return;     // already cloned
  const clone = colA.cloneNode(true) as HTMLElement;
  clone.id = 'col-b';
  clone.hidden = true;
  // Rewrite every `-a` suffixed id under the clone to `-b`.
  clone.querySelectorAll<HTMLElement>('[id]').forEach((node) => {
    if (node.id.endsWith('-a')) node.id = node.id.slice(0, -2) + '-b';
  });
  colA.parentElement!.appendChild(clone);
  // Attach toggle listeners on the brand-new <details> nodes — cloneNode
  // copies attributes but not event listeners.
  clone.querySelectorAll<HTMLDetailsElement>('details.panel').forEach((d) => {
    d.addEventListener('toggle', onPanelToggle);
  });
}

// Toggle handler: sync the matching panel in the other column AND update
// the URL hash. Both columns stay in sync when the user collapses
// something in either.
function onPanelToggle(this: HTMLDetailsElement): void {
  const m = this.id.match(/^(panel-[\w-]+)-([ab])$/);
  if (m) {
    const otherSlot = m[2] === 'a' ? 'b' : 'a';
    const other = document.getElementById(`${m[1]}-${otherSlot}`) as HTMLDetailsElement | null;
    if (other && other.open !== this.open) other.open = this.open;
  }
  writeHash();
}

function showColumn(slot: Slot, visible: boolean): void {
  const col = document.getElementById(`col-${slot}`);
  if (col) col.hidden = !visible;
  const cols = document.getElementById('player-cols');
  const isComparing = !!state.players.b.playerID;
  if (cols) cols.classList.toggle('compare', isComparing);
  // Mirror the same flag onto <body> so the page-width style can pick it
  // up. Using a class (instead of :has(.compare)) because that's portable
  // across browsers and because some users were seeing the narrow body
  // even with two warriors picked.
  document.body.classList.toggle('comparing', isComparing);
}

// ---- URL hash persistence ----
//
// Format: #code=ABC&fight=42&player=18&player2=25&lang=zh&closed=gear,buffs
//   - lang is opt-in (absent = use viewer's saved preference)
//   - player2 absent or empty = single-warrior view; present = compare mode
//   - closed lists collapsed panel keys (the panel-XX-a/b suffix is stripped
//     since both columns share collapse state)
interface HashState {
  code: string | null;
  fight: number | 'all' | null;
  player: number | null;
  player2: number | null;
  lang: Lang | null;
  closed: Set<string>;
}
function parseHash(): HashState {
  const raw = (typeof location !== 'undefined' ? location.hash : '').replace(/^#/, '');
  const p = new URLSearchParams(raw);
  const fightStr = p.get('fight');
  const playerStr = p.get('player');
  const player2Str = p.get('player2');
  const langStr = p.get('lang');
  const closedStr = p.get('closed') ?? '';
  let fight: HashState['fight'] = null;
  if (fightStr === 'all') fight = 'all';
  else if (fightStr && Number.isFinite(Number(fightStr))) fight = Number(fightStr);
  return {
    code: p.get('code') || null,
    fight,
    player: playerStr && Number.isFinite(Number(playerStr)) ? Number(playerStr) : null,
    player2: player2Str && Number.isFinite(Number(player2Str)) ? Number(player2Str) : null,
    lang: langStr === 'zh' || langStr === 'en' ? langStr : null,
    closed: new Set(closedStr.split(',').filter(Boolean)),
  };
}
function writeHash(): void {
  const p = new URLSearchParams();
  if (state.reportCode) p.set('code', state.reportCode);
  if (state.fightMode === 'all') p.set('fight', 'all');
  else if (state.selectedFightID != null) p.set('fight', String(state.selectedFightID));
  if (state.players.a.playerID != null) p.set('player', String(state.players.a.playerID));
  if (state.players.b.playerID != null) p.set('player2', String(state.players.b.playerID));
  const stored = (() => {
    try { return localStorage.getItem('wcl-warrior-analyzer.lang'); } catch { return null; }
  })();
  if (stored === 'zh' || stored === 'en') p.set('lang', stored);
  // Collapsed-panel state from col-a only — both columns share toggle state.
  const closed: string[] = [];
  document.querySelectorAll<HTMLDetailsElement>('#col-a details.panel').forEach((d) => {
    if (!d.open && d.id) {
      const m = d.id.match(/^panel-(.+)-a$/);
      if (m && m[1]) closed.push(m[1]);
    }
  });
  if (closed.length > 0) p.set('closed', closed.join(','));
  const next = `#${p.toString()}`;
  if (location.hash !== next) {
    history.replaceState(null, '', next);
  }
}

function applyClosedPanels(closed: Set<string>): void {
  document.querySelectorAll<HTMLDetailsElement>('details.panel').forEach((d) => {
    if (!d.id) return;
    const m = d.id.match(/^panel-(.+)-([ab])$/);
    if (!m || !m[1]) return;
    d.open = !closed.has(m[1]);
  });
}

function setupPanelToggleListeners(): void {
  // Wire all currently-present <details class="panel"> nodes. col-b's
  // panels get wired separately inside cloneColumnB() since they don't
  // exist yet at this point.
  document.querySelectorAll<HTMLDetailsElement>('details.panel').forEach((d) => {
    d.addEventListener('toggle', onPanelToggle);
  });
}

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ---- App state ----
//
// One PlayerSlot per column. `slotA` always represents the primary
// warrior; `slotB` is populated only when the user picks a second
// warrior in the compare dropdown.
interface PlayerSlot {
  playerID: number | null;
  parsedInfo: ParsedPlayerInfo | null;
  rawEvents: unknown[] | null;
  fightDurationSec: number;
  fightKill: boolean;
  currentBuild: TalentBuild;
  analysis: AnalysisResponse | null;
  // Per-slot load token. Each call to loadSlot/loadAnalysis bumps THIS
  // slot's counter — slot A and slot B fetch independently. Earlier code
  // shared one token between slots, so loading slot B (e.g. on a refresh
  // with player2= in the hash) invalidated slot A's in-flight fetch and
  // its data never reached state. The user noticed this as "first load
  // works but after Ctrl+R the left column is empty".
  loadToken: number;
  // Flat AP / crit% from gear, summed across all 19 items by parsing each
  // item's wowhead tooltipHtml. null until the async sweep finishes —
  // first render of the stats panel uses 0/0 fallback, second render
  // (after the sweep) shows the full character-sheet AP / Crit.
  gearStats: GearStatBonuses | null;
  // Diagnosis report (PRD-诊断分析). Loaded after the analysis lands so
  // we have all aggregator outputs ready. Null while loading or on error.
  diagnosis: DiagnosisReport | null;
}
function emptySlot(): PlayerSlot {
  return {
    playerID: null,
    parsedInfo: null,
    rawEvents: null,
    fightDurationSec: 0,
    fightKill: false,
    currentBuild: {},
    analysis: null,
    loadToken: 0,
    gearStats: null,
    diagnosis: null,
  };
}
interface AppState {
  reportCode: string | null;
  reportTitle: string;
  reportZone: { id: number; name: string } | null;
  reportTotalSec: number;
  fights: WclFight[];
  warriors: WclPlayer[];
  classRoster: string[];
  selectedFightID: number | null;
  fightMode: 'single' | 'all';
  players: { a: PlayerSlot; b: PlayerSlot };
  assumed: AssumedState;
  // 'base'   = race+class + gear, matches the in-town character sheet.
  // 'buffed' = WCL CombatantInfo values (with raid buffs / consumables /
  //            world buffs already applied), matches the in-fight char sheet.
  statsViewMode: 'base' | 'buffed';
}

const state: AppState = {
  reportCode: null,
  reportTitle: '',
  reportZone: null,
  reportTotalSec: 0,
  fights: [],
  warriors: [],
  classRoster: [],
  selectedFightID: null,
  fightMode: 'single',
  players: { a: emptySlot(), b: emptySlot() },
  assumed: loadAssumedState(),
  statsViewMode: (() => {
    try {
      const v = localStorage.getItem('wcl-warrior-analyzer.statsViewMode');
      if (v === 'base' || v === 'buffed') return v;
    } catch { /* ignore */ }
    return 'base';
  })(),
};

// ---- i18n: apply translations to DOM ----
function applyI18n(): void {
  document.title = t('app.title');
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((node) => {
    const key = node.getAttribute('data-i18n');
    if (key) node.textContent = t(key);
  });
  document.querySelectorAll<HTMLInputElement>('[data-i18n-placeholder]').forEach((node) => {
    const key = node.getAttribute('data-i18n-placeholder');
    if (key) node.placeholder = t(key);
  });
  document.querySelectorAll<HTMLElement>('[data-lang]').forEach((btn) => {
    btn.classList.toggle('active', btn.getAttribute('data-lang') === getLang());
  });
}

// ---- Theme toggle (dark/light) ----
type Theme = 'dark' | 'light';
const THEME_KEY = 'wcl-warrior-analyzer.theme';

function loadTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* ignore */ }
  return 'dark';
}
function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
  const btn = document.getElementById('theme-toggle');
  if (btn) btn.textContent = theme === 'dark' ? '🌙' : '☀️';
}
function setupThemeToggle(): void {
  const btn = document.getElementById('theme-toggle');
  if (!btn) return;
  btn.addEventListener('click', () => {
    const cur: Theme = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    applyTheme(cur === 'dark' ? 'light' : 'dark');
  });
}

function setupLangSwitcher(): void {
  el<HTMLDivElement>('lang-switcher').addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-lang]');
    if (!target) return;
    const lang = target.getAttribute('data-lang');
    if (lang === 'en' || lang === 'zh') {
      setLang(lang as Lang);
      applyI18n();
      rerenderDynamic();
      writeHash();
    }
  });
}

function rerenderDynamic(): void {
  if (state.reportCode) {
    renderFights();
    renderWarriors();
  }
  // Render slot A first then slot B — slot B's renderers read slot A's
  // resolved data for compareTo, so order matters when both have data.
  for (const slot of SLOTS) {
    if (state.players[slot].parsedInfo) renderPlayerSnapshot(slot);
    if (state.players[slot].analysis) renderSlotAnalysisPanels(slot);
  }
}

// ---- Backend health probe ----
async function checkHealth(): Promise<void> {
  const target = el<HTMLSpanElement>('health');
  try {
    const res = await fetch('/api/health');
    if (!res.ok) {
      target.textContent = `HTTP ${res.status}`;
      target.classList.add('error');
      return;
    }
    const data = (await res.json()) as HealthResponse;
    target.textContent = `${data.status} @ ${data.time}`;
    target.classList.add('ok');
  } catch (err) {
    target.textContent = err instanceof Error ? err.message : String(err);
    target.classList.add('error');
  }
}

// ---- Report loading ----
async function loadReport(rawInput: string): Promise<void> {
  const status = el<HTMLParagraphElement>('loader-status');
  const reportSection = el<HTMLElement>('report');
  const playerSection = el<HTMLElement>('player-info');

  status.className = 'status loading';
  status.textContent = t('loader.loading');
  reportSection.hidden = true;
  playerSection.hidden = true;

  state.selectedFightID = null;
  state.players.a = emptySlot();
  state.players.b = emptySlot();
  // emptySlot() resets each slot's loadToken to 0 — any in-flight fetch
  // from the previous report will see myToken !== ps.loadToken and bail.

  let res: Response;
  try {
    res = await fetch(`/api/wcl/report?url=${encodeURIComponent(rawInput)}`);
  } catch (err) {
    status.className = 'status error';
    status.textContent = t('loader.networkError', { msg: err instanceof Error ? err.message : String(err) });
    return;
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const errBody = (await res.json()) as ApiError;
      msg = errBody.details ? `${errBody.error} — ${errBody.details}` : errBody.error;
    } catch { /* keep HTTP status */ }
    status.className = 'status error';
    status.textContent = msg;
    return;
  }

  const data = (await res.json()) as ReportResponse;
  state.reportCode = data.code;
  state.reportTitle = data.title;
  state.reportZone = data.zone;
  state.reportTotalSec = Math.round((data.endTime - data.startTime) / 1000);
  state.fights = data.fights.filter((f) => NAXX_ENCOUNTER_IDS.has(f.encounterID));
  state.warriors = data.players
    .filter((p) => p.className === 'Warrior')
    .sort((a, b) => a.name.localeCompare(b.name));
  state.classRoster = data.players.map((p) => p.className);

  status.className = 'status';
  status.textContent = '';

  el<HTMLInputElement>('report-input').value = data.code;

  renderFights();
  renderWarriors();
  writeHash();
}

function renderFights(): void {
  const sel = document.getElementById('fight-select') as unknown as HTMLSelectElement;
  sel.replaceChildren();

  if (state.fights.length === 0) {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = t('report.empty.fights');
    opt.disabled = true;
    sel.appendChild(opt);
    return;
  }

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = t('report.fights');
  placeholder.disabled = true;
  if (state.selectedFightID == null && state.fightMode !== 'all') placeholder.selected = true;
  sel.appendChild(placeholder);

  if (state.fights.length >= 2) {
    const allOpt = document.createElement('option');
    allOpt.value = 'all';
    const totalSec = state.fights.reduce((acc, f) => acc + f.durationSec, 0);
    allOpt.textContent = t('report.fight.all', { n: state.fights.length, dur: fmtDuration(totalSec) });
    if (state.fightMode === 'all') allOpt.selected = true;
    sel.appendChild(allOpt);
  }

  for (const f of state.fights) {
    const opt = document.createElement('option');
    opt.value = String(f.id);
    const status = f.kill ? t('report.fight.kill') : t('report.fight.wipe');
    const bossName = tBossName(f.encounterID, f.name);
    opt.textContent = `${bossName} · ${status} · ${fmtDuration(f.durationSec)}`;
    if (state.fightMode === 'single' && state.selectedFightID === f.id) opt.selected = true;
    sel.appendChild(opt);
  }
}

function renderWarriors(): void {
  const selA = document.getElementById('warrior-select') as unknown as HTMLSelectElement;
  const selB = document.getElementById('warrior-select-b') as unknown as HTMLSelectElement;
  selA.replaceChildren();
  selB.replaceChildren();

  if (state.warriors.length === 0) {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = t('report.empty.warriors');
    opt.disabled = true;
    selA.appendChild(opt);
    selB.appendChild(opt.cloneNode(true));
    return;
  }

  // Slot A: placeholder + warrior list. No "(no compare)" — slot A is required.
  const phA = document.createElement('option');
  phA.value = '';
  phA.textContent = t('report.warriors');
  phA.disabled = true;
  if (state.players.a.playerID == null) phA.selected = true;
  selA.appendChild(phA);

  // Slot B: leading "(no compare)" option to allow exiting the compare mode.
  const phB = document.createElement('option');
  phB.value = '';
  phB.textContent = t('report.warriors.compare');
  if (state.players.b.playerID == null) phB.selected = true;
  selB.appendChild(phB);

  for (const p of state.warriors) {
    for (const sel of [selA, selB] as const) {
      const opt = document.createElement('option');
      opt.value = String(p.id);
      const server = p.server ? ` (${p.server})` : '';
      opt.textContent = `${p.name}${server}`;
      if ((sel === selA && state.players.a.playerID === p.id) ||
          (sel === selB && state.players.b.playerID === p.id)) {
        opt.selected = true;
      }
      sel.appendChild(opt);
    }
  }
}

// ---- Selection ----
function selectFight(fightID: number): void {
  state.fightMode = 'single';
  state.selectedFightID = fightID;
  writeHash();
  // Reload BOTH slots when fight changes — comparison only makes sense
  // when both warriors are looking at the same fight.
  for (const slot of SLOTS) {
    if (state.players[slot].playerID != null) void loadSlot(slot);
  }
}

function selectAllBosses(): void {
  state.fightMode = 'all';
  state.selectedFightID = state.fights[0]?.id ?? null;
  writeHash();
  for (const slot of SLOTS) {
    if (state.players[slot].playerID != null) void loadSlot(slot);
  }
}

function selectPlayer(slot: Slot, playerID: number | null): void {
  state.players[slot].playerID = playerID;
  if (playerID == null) {
    // Wipe slot data and hide its column.
    state.players[slot] = emptySlot();
    if (slot === 'b') {
      // Remove the comparing-mode flags both on the cols container and on
      // <body>, so the page-width rule reverts to single-warrior 72rem.
      const colsRoot = document.getElementById('player-cols');
      if (colsRoot) colsRoot.classList.toggle('compare', false);
      document.body.classList.toggle('comparing', false);
      const col = document.getElementById('col-b');
      if (col) col.hidden = true;
    }
    writeHash();
    return;
  }
  // For slot B, reveal the column the moment a player is picked.
  if (slot === 'b') showColumn('b', true);
  writeHash();
  void loadSlot(slot);
}

// Loads CombatantInfo + analysis for a single slot. All UI for that slot
// renders into `#col-{slot}` elements, identified via slotEl(). Per-slot
// load token means slot A and slot B fetch independently — slot B's token
// bump can't invalidate slot A's in-flight fetch.
async function loadSlot(slot: Slot): Promise<void> {
  if (!state.reportCode || state.selectedFightID == null) return;
  const ps = state.players[slot];
  if (ps.playerID == null) return;

  const code = state.reportCode;
  const fightID = state.selectedFightID;
  const playerID = ps.playerID;

  ps.loadToken += 1;
  const myToken = ps.loadToken;

  const section = el<HTMLElement>('player-info');
  const status = el<HTMLParagraphElement>('player-info-status');
  const body = el<HTMLElement>('player-info-body');

  section.hidden = false;
  body.hidden = false;
  status.hidden = true;

  let res: Response;
  try {
    const params = new URLSearchParams({
      code,
      fightID: String(fightID),
      playerID: String(playerID),
    });
    res = await fetch(`/api/wcl/playerInfo?${params}`);
  } catch (err) {
    if (myToken !== ps.loadToken) return;
    status.hidden = false;
    status.className = 'status error';
    status.textContent = t('loader.networkError', { msg: err instanceof Error ? err.message : String(err) });
    return;
  }
  if (myToken !== ps.loadToken) return;

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const errBody = (await res.json()) as ApiError;
      msg = errBody.details ? `${errBody.error} — ${errBody.details}` : errBody.error;
    } catch { /* keep HTTP status */ }
    status.hidden = false;
    status.className = 'status error';
    status.textContent = msg;
    return;
  }

  const data = (await res.json()) as PlayerInfoResponse;
  if (myToken !== ps.loadToken) return;

  const fightDurationMs = data.fightEnd - data.fightStart;
  const parsed = parseCombatantInfo(data.combatantInfoEvents, data.buffsTable, fightDurationMs);
  if (!parsed) {
    status.hidden = false;
    status.className = 'status error';
    status.textContent = t('player.noEvents');
    return;
  }

  ps.parsedInfo = parsed;
  ps.rawEvents = data.combatantInfoEvents;
  ps.fightDurationSec = data.fightDurationSec;
  ps.fightKill = data.fightKill;
  ps.currentBuild = { ...parsed.inferredBuild };
  ps.analysis = null;
  ps.gearStats = null;

  renderPlayerSnapshot(slot);
  // Slot A finishing changes the compareTo target slot B uses. Re-render
  // slot B's snapshot if it already has data so the stat deltas appear.
  if (slot === 'a' && state.players.b.parsedInfo) {
    renderPlayerSnapshot('b');
  }
  // Kick off the async tooltip sweep that sums "+N attack power" /
  // "+N% crit" lines across all 19 gear pieces. When it finishes we
  // re-render the stats row with the full character-sheet AP / Crit.
  void computeGearStats(slot, myToken);
  void loadAnalysis(slot, myToken);
}

// Walks every gear piece's wowhead tooltip AND each piece's permanent
// enchant spell description, summing every "+N <stat>" / "+N attack
// power" / "+N% crit" line. wowhead lookups are memoised in
// src/wowhead.ts, and we already trigger most of them from renderGear,
// so each Promise resolves quickly on the second pass. Bounded work:
// 19 item lookups + up to 19 enchant spell lookups per slot.
//
// Why enchants matter: a fully twinked vanilla warrior carries ~9
// permanent enchants giving roughly +30 strength, +10 agility, +1% crit,
// +26 AP. Without these, the displayed Str/AP/Crit lags the in-game
// character sheet by 30+ str / 60+ AP / 1+ %.
async function computeGearStats(slot: Slot, myToken: number): Promise<void> {
  const ps = state.players[slot];
  const parsed = ps.parsedInfo;
  if (!parsed) return;
  let str = 0, agi = 0, sta = 0, int_ = 0, spi = 0;
  let ap = 0;
  let crit = 0;
  let armor = 0;
  const accumulate = (b: GearStatBonuses): void => {
    str  += b.str;
    agi  += b.agi;
    sta  += b.sta;
    int_ += b.int;
    spi  += b.spi;
    ap   += b.ap;
    crit += b.crit;
    armor += b.armor;
  };

  // Resolve permanent-enchant effect IDs → spell IDs upfront. The
  // wowsims db (shared/enchants.json) doesn't carry every enchant; for
  // anything missing we just skip that slot's enchant rather than
  // double-counting or failing the whole sweep.
  const enchantSpellIds: number[] = [];
  for (const g of parsed.gear) {
    if (!g.permanentEnchant) continue;
    const e = lookupEnchant(g.permanentEnchant);
    if (e?.spellId) enchantSpellIds.push(e.spellId);
  }

  await Promise.allSettled([
    ...parsed.gear
      .filter((g) => g.itemId > 0)
      .map(async (g) => {
        const r = await lookup('item', g.itemId);
        if (!r.tooltipHtml) return;
        const b = extractGearStats(r.tooltipHtml);
        // Zero out armor for weapon / off-hand-non-shield / ranged slots —
        // vanilla weapon tooltips include a "140护甲" line that does NOT
        // contribute to the player's armor pool. Without this, summing
        // weapons would inflate armor by ~280-400 (typical weapon "armor"
        // is 100-150 each). Slot indexes per shared/types.ts EQUIPMENT_SLOT_NAMES:
        //   15 Main Hand, 16 Off Hand (could be shield — but shields are
        //   rare for fury/arms; prot warriors add a separate fix later),
        //   17 Ranged, 1 Neck, 3 Shirt, 10/11 Fingers, 12/13 Trinkets,
        //   18 Tabard. None of those contribute armor in vanilla.
        const slotIdx = g.slotIndex;
        const noArmorSlot = slotIdx === 15 || slotIdx === 16 || slotIdx === 17 ||
                            slotIdx === 1  || slotIdx === 3  ||
                            slotIdx === 10 || slotIdx === 11 ||
                            slotIdx === 12 || slotIdx === 13 ||
                            slotIdx === 18;
        if (noArmorSlot) b.armor = 0;
        accumulate(b);
      }),
    ...enchantSpellIds.map(async (spellId) => {
      const r = await lookup('spell', spellId);
      // Spell pages give us description (clean text) — feed it through
      // the same parser as the item tooltips. The parser handles enchant
      // formats ("力量+9", "力量提高 8 点", "Strength by N", "+N to all stats").
      // Enchant armor (Lesser Armor +50 cloak, etc.) is fine to add — it's
      // genuinely +50 armor.
      if (r.description) accumulate(extractGearStats(r.description));
    }),
  ]);
  // If the user loaded a different player while we were summing, abandon.
  if (myToken !== ps.loadToken) return;
  ps.gearStats = { str, agi, sta, int: int_, spi, ap, crit, armor };
  // Re-render this slot's stats row with the full numbers.
  renderPlayerSnapshot(slot);
  // Slot A's bonuses changed → slot B's compareTo deltas need to refresh too.
  if (slot === 'a' && state.players.b.parsedInfo) {
    renderPlayerSnapshot('b');
  }
}

async function loadAnalysis(slot: Slot, myToken: number): Promise<void> {
  if (!state.reportCode) return;
  const ps = state.players[slot];
  if (ps.playerID == null) return;
  if (state.fightMode === 'single' && state.selectedFightID == null) return;
  const code = state.reportCode;
  const fightID = state.selectedFightID;
  const playerID = ps.playerID;
  const isAll = state.fightMode === 'all';

  const status = slotEl<HTMLParagraphElement>(slot, 'analysis-status');
  const body = slotEl<HTMLDivElement>(slot, 'analysis-body');
  status.hidden = false;
  status.className = 'status muted';
  status.textContent = isAll ? t('panel.analysis.loadingAll') : t('panel.analysis.loading');
  body.replaceChildren();

  let res: Response;
  try {
    const params = isAll
      ? new URLSearchParams({ code, playerID: String(playerID) })
      : new URLSearchParams({ code, fightID: String(fightID), playerID: String(playerID) });
    const path = isAll ? '/api/wcl/analysisAll' : '/api/wcl/analysis';
    res = await fetch(`${path}?${params}`);
  } catch (err) {
    if (myToken !== ps.loadToken) return;
    status.className = 'status error';
    status.textContent = t('panel.analysis.error', { msg: err instanceof Error ? err.message : String(err) });
    return;
  }
  if (myToken !== ps.loadToken) return;

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const errBody = (await res.json()) as ApiError;
      msg = errBody.details ? `${errBody.error} — ${errBody.details}` : errBody.error;
    } catch { /* keep HTTP status */ }
    status.className = 'status error';
    status.textContent = t('panel.analysis.error', { msg });
    return;
  }

  const data = (await res.json()) as AnalysisResponse;
  if (myToken !== ps.loadToken) return;
  ps.analysis = data;
  status.hidden = true;

  renderSlotAnalysisPanels(slot);
  // If we just finished slot A and slot B already has data, slot B's
  // ability table needs to re-render so it can pick up slot A as the
  // compareTo (reordering rows + adding ▲/▼ deltas). Token races aren't
  // an issue here because we're operating on already-resolved state.
  if (slot === 'a' && state.players.b.analysis) {
    renderSlotAnalysisPanels('b');
  }

  // Diagnosis depends on the analysis output and the current assumed-buff
  // selection. Kicked off here, async; the rankings line is filled after
  // a separate async fetch.
  void loadDiagnosis(slot, myToken);
}

// Diagnosis fetch — calls /api/wcl/diagnosis with current assumed-buff
// selections, renders the report into the diagnosis panel, then fires off
// the rankings fetch in parallel and patches the percentile line in.
async function loadDiagnosis(slot: Slot, myToken: number): Promise<void> {
  if (!state.reportCode) return;
  const ps = state.players[slot];
  if (ps.playerID == null) return;
  if (state.fightMode === 'single' && state.selectedFightID == null) return;

  const code = state.reportCode;
  const isAll = state.fightMode === 'all';
  // playerInfo always needs a concrete fightID (CombatantInfo is per-fight),
  // even in "all bosses" aggregate mode. selectAllBosses() picks the first
  // fight as the representative one — re-use that here.
  const fightID = state.selectedFightID;
  const playerID = ps.playerID;
  if (fightID == null) return;

  const status = slotEl<HTMLParagraphElement>(slot, 'diagnosis-status');
  const body = slotEl<HTMLDivElement>(slot, 'diagnosis-body');
  status.hidden = false;
  status.className = 'status muted';
  status.textContent = t('panel.diagnosis.loading');
  body.replaceChildren();

  // Build assumed-buffs list from the current state (same source the
  // buffed-stats view uses, so the diagnosis matches what the user sees).
  const assumedIds = activeAssumedSpellIds(state.assumed, state.classRoster);
  const params = new URLSearchParams({
    code,
    fightID: String(fightID),
    playerID: String(playerID),
    assumedBuffs: assumedIds.join(','),
  });
  if (isAll) params.set('mode', 'all');

  let res: Response;
  try {
    res = await fetch(`/api/wcl/diagnosis?${params}`);
  } catch (err) {
    if (myToken !== ps.loadToken) return;
    status.className = 'status error';
    status.textContent = t('panel.diagnosis.error', { msg: err instanceof Error ? err.message : String(err) });
    return;
  }
  if (myToken !== ps.loadToken) return;

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const errBody = (await res.json()) as ApiError;
      msg = errBody.details ? `${errBody.error} — ${errBody.details}` : errBody.error;
    } catch { /* keep HTTP status */ }
    status.className = 'status error';
    status.textContent = t('panel.diagnosis.error', { msg });
    return;
  }

  const report = (await res.json()) as DiagnosisReport;
  if (myToken !== ps.loadToken) return;
  ps.diagnosis = report;
  status.hidden = true;

  renderDiagnosis(report, body);

  // Async rankings fetch — does NOT block the main panel, fills percentile
  // line when ready. Failures show "—" via fillRankings(null).
  void fetchAndFillRankings(slot, myToken, report);
}

// Pull the WCL Rankings for this player + boss and patch the diagnosis
// panel's percentile line. Best-effort — silently fails to "—" on error
// so the rest of the panel stays useful.
async function fetchAndFillRankings(slot: Slot, myToken: number, report: DiagnosisReport): Promise<void> {
  const ps = state.players[slot];
  const player = state.warriors.find((w) => w.id === ps.playerID);
  if (!player) return;

  // Encounter ID — only meaningful for single-fight reports. For 'all'
  // (across-fights aggregate) percentile doesn't apply since rankings are
  // per-encounter.
  if (report.fightId === 'all' || typeof report.fightId !== 'number') {
    const body = slotEl<HTMLDivElement>(slot, 'diagnosis-body');
    fillRankings(body, null);
    return;
  }
  const fight = state.fights.find((f) => f.id === report.fightId);
  if (!fight) return;

  const params = new URLSearchParams({
    name: player.name,
    server: player.server,
    region: 'cn',                  // PM is on CN realms; PRD adjusts later if needed
    encounterID: String(fight.encounterID),
    class: 'Warrior',
    spec: 'Fury',
    metric: 'dps',
    subdomain: 'vanilla',
  });

  try {
    const res = await fetch(`/api/wcl/rankings?${params}`);
    if (myToken !== ps.loadToken) return;
    if (!res.ok) {
      const body = slotEl<HTMLDivElement>(slot, 'diagnosis-body');
      fillRankings(body, null);
      return;
    }
    const data = (await res.json()) as RankingsFillData;
    if (myToken !== ps.loadToken) return;
    const body = slotEl<HTMLDivElement>(slot, 'diagnosis-body');
    fillRankings(body, data);
  } catch {
    if (myToken !== ps.loadToken) return;
    const body = slotEl<HTMLDivElement>(slot, 'diagnosis-body');
    fillRankings(body, null);
  }
}

// Renders all five Phase 2A panels for the given slot. In compare mode
// slot B's ability breakdown gets compareTo=slot A so the table reorders
// + shows per-cell deltas; the other panels render normally for now.
function renderSlotAnalysisPanels(slot: Slot): void {
  const ps = state.players[slot];
  const data = ps.analysis;
  if (!data) return;
  const isAll = state.fightMode === 'all';
  const cmpAnalysis = slot === 'b' ? state.players.a.analysis : null;

  const body = slotEl<HTMLDivElement>(slot, 'analysis-body');
  renderAbilityBreakdown(data, body, cmpAnalysis ? { compareTo: cmpAnalysis } : undefined);

  const rageBody = slotEl<HTMLDivElement>(slot, 'rage-body');
  if (data.rage) {
    renderRagePanel(data.rage, rageBody, cmpAnalysis?.rage ? { compareTo: cmpAnalysis.rage } : undefined);
  } else rageBody.replaceChildren();

  const cdBody = slotEl<HTMLDivElement>(slot, 'cd-body');
  if (data.cooldowns) renderCooldownPanel(data.cooldowns, cdBody, isAll);
  else cdBody.replaceChildren();

  const debuffsBody = slotEl<HTMLDivElement>(slot, 'debuffs-body');
  if (data.debuffs) renderDebuffPanel(data.debuffs, debuffsBody);
  else debuffsBody.replaceChildren();

  const rotationBody = slotEl<HTMLDivElement>(slot, 'rotation-body');
  if (data.rotation) renderRotationPanel(data.rotation, rotationBody, isAll);
  else rotationBody.replaceChildren();
}

function renderPlayerSnapshot(slot: Slot): void {
  const ps = state.players[slot];
  const parsed = ps.parsedInfo;
  if (!parsed) return;

  const raw = slotEl<HTMLPreElement>(slot, 'player-info-raw');
  if (ps.rawEvents) raw.textContent = JSON.stringify(ps.rawEvents, null, 2);

  // Overview
  slotEl<HTMLParagraphElement>(slot, 'overview-spec').innerHTML =
    `${t('overview.spec')}: <strong class="emph">${t(`spec.${parsed.spec}`)}</strong> &nbsp; ` +
    `<span class="muted">${t('overview.treePoints', {
      arms: parsed.treePoints.arms,
      fury: parsed.treePoints.fury,
      prot: parsed.treePoints.protection,
    })}</span>`;
  // Slot B compares against slot A's full parsedInfo + flat bonuses so
  // the renderer can compute character-sheet AP / Crit deltas using the
  // full numbers (attribute-derived + flat gear bonuses). Until the
  // async tooltip sweep finishes, gearStats is null and we fall back
  // to "no flat bonuses" — same display as before, just temporarily.
  const cmpParsed = slot === 'b' ? state.players.a.parsedInfo : null;
  const cmpGearStats = slot === 'b' ? state.players.a.gearStats : null;
  // Active buff effects from the assumed picker — feed both slots the same
  // effect list (single-raid scenario; both warriors share buff selections).
  const activeBuffs = activeAssumedEffects(state.assumed, state.classRoster);
  renderStats(parsed, slotEl<HTMLDivElement>(slot, 'overview-stats'), {
    compareTo: cmpParsed ?? undefined,
    gearStats: ps.gearStats ?? undefined,
    compareGearStats: cmpGearStats ?? undefined,
    viewMode: state.statsViewMode,
    buffEffects: activeBuffs,
    compareBuffEffects: activeBuffs,    // same buff selections for both
    onToggleViewMode: (mode) => {
      state.statsViewMode = mode;
      try { localStorage.setItem('wcl-warrior-analyzer.statsViewMode', mode); } catch { /* ignore */ }
      // Re-render BOTH slots so they stay in sync.
      for (const s of SLOTS) {
        if (state.players[s].parsedInfo) renderPlayerSnapshot(s);
      }
    },
  });

  renderGear(parsed.gear, slotEl<HTMLDivElement>(slot, 'gear-body'));

  // Assumed-buff picker + aura list. Both slots share state.assumed; toggling
  // in either column updates the global state, then we re-render BOTH slots'
  // aura panels so their lists stay in sync.
  const renderAuraPanel = (): void => {
    for (const s of SLOTS) {
      const ps2 = state.players[s];
      const parsed2 = ps2.parsedInfo;
      if (!parsed2) continue;
      renderAssumedPicker(slotEl<HTMLDivElement>(s, 'assumed-picker'), state.assumed, state.classRoster, renderAuraPanel);
      const assumedIds = activeAssumedSpellIds(state.assumed, state.classRoster);
      const assumedAuras: ParsedPlayerInfo['auras'] = assumedIds.map((id) => ({
        abilityId: id,
        name: `spell ${id}`,
        icon: raidBuffIcon(id) ?? '',
        source: 0,
        stacks: 1,
        origin: 'assumed' as const,
      }));
      const existingIds = new Set(parsed2.auras.map((a) => a.abilityId));
      const merged = [...assumedAuras.filter((a) => !existingIds.has(a.abilityId)), ...parsed2.auras];
      renderAuras(merged, slotEl<HTMLDivElement>(s, 'aura-body'));
      for (const a of assumedAuras) {
        void lookup('spell', a.abilityId).then((r) => {
          if (r.name) a.name = r.name;
        });
      }
      // Diagnosis depends on the assumed-buff selection (M7 missing buff
      // detection). Refire the diagnosis fetch whenever the user toggles
      // a buff. The token check inside loadDiagnosis ensures stale fetches
      // get discarded.
      if (ps2.analysis) {
        void loadDiagnosis(s, ps2.loadToken);
      }
    }
  };
  renderAuraPanel();

  renderTalents(parsed, slotEl<HTMLDivElement>(slot, 'talent-body'), {
    build: ps.currentBuild,
    onChange: (b) => { ps.currentBuild = b; },
  });
}

// ---- Wire up ----
applyTheme(loadTheme());
setupThemeToggle();

const initialHash = parseHash();
if (initialHash.lang) {
  setLang(initialHash.lang, true);
} else {
  setLang(detectLang(), false);
}
applyI18n();
setupLangSwitcher();

// Clone column A → column B BEFORE wiring panel-toggle listeners so col-b's
// panels also get them.
cloneColumnB();
setupPanelToggleListeners();

const form = el<HTMLFormElement>('report-form');
form.addEventListener('submit', (e) => {
  e.preventDefault();
  const input = el<HTMLInputElement>('report-input');
  const value = input.value.trim();
  if (value) void loadReport(value);
});

const fightSelectEl = document.getElementById('fight-select') as unknown as HTMLSelectElement;
const warriorSelectEl = document.getElementById('warrior-select') as unknown as HTMLSelectElement;
const warriorSelectBEl = document.getElementById('warrior-select-b') as unknown as HTMLSelectElement;
fightSelectEl.addEventListener('change', (e) => {
  const v = (e.target as HTMLSelectElement).value;
  if (v === 'all') selectAllBosses();
  else if (v) selectFight(Number.parseInt(v, 10));
});
warriorSelectEl.addEventListener('change', (e) => {
  const v = (e.target as HTMLSelectElement).value;
  if (v) selectPlayer('a', Number.parseInt(v, 10));
});
warriorSelectBEl.addEventListener('change', (e) => {
  const v = (e.target as HTMLSelectElement).value;
  selectPlayer('b', v ? Number.parseInt(v, 10) : null);
});

void checkHealth();

applyClosedPanels(initialHash.closed);
void (async () => {
  if (!initialHash.code) return;
  el<HTMLInputElement>('report-input').value = initialHash.code;
  await loadReport(initialHash.code);
  if (initialHash.fight === 'all' && state.fights.length > 0) {
    fightSelectEl.value = 'all';
    selectAllBosses();
  } else if (typeof initialHash.fight === 'number' && state.fights.some((f) => f.id === initialHash.fight)) {
    fightSelectEl.value = String(initialHash.fight);
    selectFight(initialHash.fight);
  }
  if (initialHash.player != null && state.warriors.some((w) => w.id === initialHash.player)) {
    warriorSelectEl.value = String(initialHash.player);
    selectPlayer('a', initialHash.player);
  }
  if (initialHash.player2 != null && state.warriors.some((w) => w.id === initialHash.player2)) {
    warriorSelectBEl.value = String(initialHash.player2);
    selectPlayer('b', initialHash.player2);
  }
})();
